import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { FileText, CreditCard, Clock, Scale, Shield, AlertTriangle, XCircle, Gavel } from 'lucide-react';

const translations = {
  nl: {
    title: 'Algemene Voorwaarden',
    subtitle: 'Laatst bijgewerkt: maart 2026',
    intro: 'Deze algemene voorwaarden zijn van toepassing op alle offertes, overeenkomsten en diensten van Netblitz, gevestigd te Robert Schumandomein 2, 6229ES Maastricht, Nederland.',
    sections: [
      {
        icon: FileText,
        title: '1. Definities',
        content: `In deze voorwaarden wordt verstaan onder:
• "Netblitz": de dienstverlener, gevestigd te Maastricht
• "Opdrachtgever": de natuurlijke of rechtspersoon die een overeenkomst aangaat met Netblitz
• "Overeenkomst": de afspraken tussen Netblitz en opdrachtgever betreffende de te leveren diensten
• "Website": het door Netblitz te ontwikkelen of onderhouden internetplatform
• "Diensten": alle werkzaamheden die Netblitz verricht, waaronder webdesign, webontwikkeling, SEO en onderhoud`
      },
      {
        icon: Scale,
        title: '2. Toepasselijkheid',
        content: `• Deze voorwaarden zijn van toepassing op alle aanbiedingen, offertes en overeenkomsten
• Afwijkingen van deze voorwaarden zijn alleen geldig indien schriftelijk overeengekomen
• Eventuele inkoop- of andere voorwaarden van de opdrachtgever zijn niet van toepassing
• Bij tegenstrijdigheden prevaleren specifieke projectafspraken boven deze algemene voorwaarden`
      },
      {
        icon: CreditCard,
        title: '3. Offertes en Prijzen',
        content: `• Alle offertes zijn vrijblijvend en 30 dagen geldig
• Prijzen zijn exclusief BTW, tenzij anders vermeld
• Bij aanvaarding van de offerte is een aanbetaling van 50% verschuldigd vóór aanvang van de werkzaamheden
• Het resterende bedrag wordt gefactureerd bij oplevering van het project
• Netblitz behoudt het recht prijzen aan te passen bij wijzigingen in de opdracht
• Meerwerk wordt vooraf besproken en gefactureerd tegen het overeengekomen uurtarief
• Betaling geschiedt volgens de op de factuur vermelde betalingstermijn (standaard 14 dagen)
• Bij niet-tijdige betaling is Netblitz gerechtigd wettelijke rente in rekening te brengen`
      },
      {
        icon: Clock,
        title: '4. Uitvoering en Levertijd',
        content: `• Netblitz voert de opdracht naar beste inzicht en vermogen uit
• Opgegeven levertijden zijn indicatief en geen fatale termijnen
• Vertraging door toedoen van de opdrachtgever (bijv. late aanlevering content) verlengt de levertijd
• De opdrachtgever is verantwoordelijk voor tijdige aanlevering van teksten, afbeeldingen en andere benodigde materialen
• Netblitz heeft het recht derden in te schakelen voor de uitvoering van de opdracht`
      },
      {
        icon: Shield,
        title: '5. Intellectueel Eigendom',
        content: `• Het auteursrecht op door Netblitz ontwikkelde ontwerpen, code en materialen berust bij Netblitz
• Na volledige betaling verkrijgt de opdrachtgever een gebruiksrecht voor de overeengekomen doeleinden
• Het is niet toegestaan de geleverde producten te verkopen, verhuren of op andere wijze ter beschikking te stellen aan derden
• Netblitz behoudt het recht om het werk te gebruiken voor eigen promotie en portfolio
• Open source componenten behouden hun oorspronkelijke licentie`
      },
      {
        icon: AlertTriangle,
        title: '6. Aansprakelijkheid',
        content: `• Netblitz is niet aansprakelijk voor indirecte schade, gevolgschade of gederfde winst
• De aansprakelijkheid is beperkt tot het bedrag dat voor de betreffende opdracht is betaald
• Netblitz is niet aansprakelijk voor schade door onjuiste of onvolledige informatie van de opdrachtgever
• De opdrachtgever vrijwaart Netblitz voor aanspraken van derden met betrekking tot de geleverde content
• Netblitz is niet aansprakelijk voor downtime van hosting of externe diensten`
      },
      {
        icon: XCircle,
        title: '7. Opzegging en Annulering',
        content: `• Beide partijen kunnen de overeenkomst schriftelijk opzeggen met een termijn van 30 dagen
• Bij annulering door de opdrachtgever zijn de tot dan toe gemaakte kosten verschuldigd
• Bij annulering na start van het project is minimaal 50% van de projectsom verschuldigd
• Netblitz mag de overeenkomst ontbinden bij wanbetaling of het niet nakomen van verplichtingen
• Na beëindiging blijven geheimhoudingsverplichtingen van kracht`
      },
      {
        icon: Gavel,
        title: '8. Toepasselijk Recht',
        content: `• Op alle overeenkomsten is Nederlands recht van toepassing
• Geschillen worden voorgelegd aan de bevoegde rechter te Maastricht
• Partijen zullen eerst proberen geschillen in onderling overleg op te lossen

Netblitz
Robert Schumandomein 2
6229ES Maastricht, Nederland
E-mail: hello@netblitz.eu
Telefoon: +31 85 115 5703`
      }
    ]
  },
  en: {
    title: 'Terms and Conditions',
    subtitle: 'Last updated: March 2026',
    intro: 'These terms and conditions apply to all quotations, agreements and services of Netblitz, located at Robert Schumandomein 2, 6229ES Maastricht, Netherlands.',
    sections: [
      {
        icon: FileText,
        title: '1. Definitions',
        content: `In these terms, the following definitions apply:
• "Netblitz": the service provider, located in Maastricht
• "Client": the natural or legal person entering into an agreement with Netblitz
• "Agreement": the arrangements between Netblitz and client regarding services to be delivered
• "Website": the internet platform to be developed or maintained by Netblitz
• "Services": all activities performed by Netblitz, including web design, web development, SEO and maintenance`
      },
      {
        icon: Scale,
        title: '2. Applicability',
        content: `• These terms apply to all offers, quotations and agreements
• Deviations from these terms are only valid if agreed in writing
• Any purchase or other terms of the client do not apply
• In case of contradictions, specific project agreements prevail over these general terms`
      },
      {
        icon: CreditCard,
        title: '3. Quotations and Prices',
        content: `• All quotations are non-binding and valid for 30 days
• Prices are exclusive of VAT, unless otherwise stated
• Upon acceptance of the quotation, a 50% deposit is due before commencement of work
• The remaining amount will be invoiced upon project delivery
• Netblitz reserves the right to adjust prices when the assignment changes
• Additional work will be discussed in advance and invoiced at the agreed hourly rate
• Payment is due according to the payment term stated on the invoice (standard 14 days)
• In case of late payment, Netblitz is entitled to charge statutory interest`
      },
      {
        icon: Clock,
        title: '4. Execution and Delivery Time',
        content: `• Netblitz executes the assignment to the best of its ability
• Stated delivery times are indicative and not strict deadlines
• Delays caused by the client (e.g., late delivery of content) extend the delivery time
• The client is responsible for timely delivery of texts, images and other required materials
• Netblitz has the right to engage third parties for the execution of the assignment`
      },
      {
        icon: Shield,
        title: '5. Intellectual Property',
        content: `• Copyright on designs, code and materials developed by Netblitz remains with Netblitz
• After full payment, the client obtains a right of use for the agreed purposes
• It is not permitted to sell, rent or otherwise make the delivered products available to third parties
• Netblitz retains the right to use the work for its own promotion and portfolio
• Open source components retain their original license`
      },
      {
        icon: AlertTriangle,
        title: '6. Liability',
        content: `• Netblitz is not liable for indirect damage, consequential damage or lost profits
• Liability is limited to the amount paid for the relevant assignment
• Netblitz is not liable for damage due to incorrect or incomplete information from the client
• The client indemnifies Netblitz against claims from third parties regarding delivered content
• Netblitz is not liable for downtime of hosting or external services`
      },
      {
        icon: XCircle,
        title: '7. Termination and Cancellation',
        content: `• Both parties can terminate the agreement in writing with a 30-day notice period
• In case of cancellation by the client, costs incurred up to that point are due
• Cancellation after project start requires payment of at least 50% of the project sum
• Netblitz may dissolve the agreement in case of non-payment or failure to meet obligations
• After termination, confidentiality obligations remain in effect`
      },
      {
        icon: Gavel,
        title: '8. Applicable Law',
        content: `• Dutch law applies to all agreements
• Disputes will be submitted to the competent court in Maastricht
• Parties will first try to resolve disputes through mutual consultation

Netblitz
Robert Schumandomein 2
6229ES Maastricht, Netherlands
Email: hello@netblitz.eu
Phone: +31 85 115 5703`
      }
    ]
  },
  fr: {
    title: 'Conditions Générales',
    subtitle: 'Dernière mise à jour: mars 2026',
    intro: 'Ces conditions générales s\'appliquent à tous les devis, accords et services de Netblitz, situé à Robert Schumandomein 2, 6229ES Maastricht, Pays-Bas.',
    sections: [
      {
        icon: FileText,
        title: '1. Définitions',
        content: `Dans ces conditions, les définitions suivantes s'appliquent:
• "Netblitz": le prestataire de services, situé à Maastricht
• "Client": la personne physique ou morale concluant un accord avec Netblitz
• "Accord": les arrangements entre Netblitz et le client concernant les services à fournir
• "Site web": la plateforme internet à développer ou maintenir par Netblitz
• "Services": toutes les activités réalisées par Netblitz, y compris conception web, développement, SEO et maintenance`
      },
      {
        icon: Scale,
        title: '2. Applicabilité',
        content: `• Ces conditions s'appliquent à toutes les offres, devis et accords
• Les dérogations à ces conditions ne sont valables que si convenues par écrit
• Les conditions d'achat ou autres du client ne s'appliquent pas
• En cas de contradictions, les accords de projet spécifiques prévalent`
      },
      {
        icon: CreditCard,
        title: '3. Devis et Prix',
        content: `• Tous les devis sont sans engagement et valables 30 jours
• Les prix sont hors TVA, sauf indication contraire
• Lors de l'acceptation du devis, un acompte de 50% est dû avant le début des travaux
• Le montant restant sera facturé à la livraison du projet
• Netblitz se réserve le droit d'ajuster les prix en cas de modification de la mission
• Les travaux supplémentaires seront discutés à l'avance et facturés au tarif horaire convenu
• Le paiement est dû selon le délai indiqué sur la facture (standard 14 jours)
• En cas de retard de paiement, Netblitz est en droit de facturer des intérêts légaux`
      },
      {
        icon: Clock,
        title: '4. Exécution et Délai de Livraison',
        content: `• Netblitz exécute la mission au mieux de ses capacités
• Les délais de livraison indiqués sont indicatifs et non contraignants
• Les retards causés par le client prolongent le délai de livraison
• Le client est responsable de la livraison en temps voulu des textes, images et autres matériaux
• Netblitz a le droit de faire appel à des tiers pour l'exécution de la mission`
      },
      {
        icon: Shield,
        title: '5. Propriété Intellectuelle',
        content: `• Les droits d'auteur sur les conceptions, codes et matériaux développés restent chez Netblitz
• Après paiement complet, le client obtient un droit d'utilisation pour les fins convenues
• Il est interdit de vendre, louer ou mettre à disposition les produits livrés à des tiers
• Netblitz conserve le droit d'utiliser le travail pour sa propre promotion et portfolio
• Les composants open source conservent leur licence d'origine`
      },
      {
        icon: AlertTriangle,
        title: '6. Responsabilité',
        content: `• Netblitz n'est pas responsable des dommages indirects ou pertes de profits
• La responsabilité est limitée au montant payé pour la mission concernée
• Netblitz n'est pas responsable des dommages dus à des informations incorrectes du client
• Le client indemnise Netblitz contre les réclamations de tiers concernant le contenu livré
• Netblitz n'est pas responsable des temps d'arrêt d'hébergement ou services externes`
      },
      {
        icon: XCircle,
        title: '7. Résiliation et Annulation',
        content: `• Les deux parties peuvent résilier l'accord par écrit avec un préavis de 30 jours
• En cas d'annulation par le client, les frais encourus sont dus
• L'annulation après le début du projet nécessite le paiement d'au moins 50% du montant
• Netblitz peut résilier l'accord en cas de non-paiement ou de manquement aux obligations
• Après résiliation, les obligations de confidentialité restent en vigueur`
      },
      {
        icon: Gavel,
        title: '8. Droit Applicable',
        content: `• Le droit néerlandais s'applique à tous les accords
• Les litiges seront soumis au tribunal compétent de Maastricht
• Les parties essaieront d'abord de résoudre les litiges par consultation mutuelle

Netblitz
Robert Schumandomein 2
6229ES Maastricht, Pays-Bas
Email: hello@netblitz.eu
Téléphone: +31 85 115 5703`
      }
    ]
  },
  de: {
    title: 'Allgemeine Geschäftsbedingungen',
    subtitle: 'Zuletzt aktualisiert: März 2026',
    intro: 'Diese Allgemeinen Geschäftsbedingungen gelten für alle Angebote, Vereinbarungen und Dienstleistungen von Netblitz, ansässig in Robert Schumandomein 2, 6229ES Maastricht, Niederlande.',
    sections: [
      {
        icon: FileText,
        title: '1. Definitionen',
        content: `In diesen Bedingungen gelten folgende Definitionen:
• "Netblitz": der Dienstleister mit Sitz in Maastricht
• "Auftraggeber": die natürliche oder juristische Person, die eine Vereinbarung mit Netblitz eingeht
• "Vereinbarung": die Absprachen zwischen Netblitz und Auftraggeber bezüglich der zu erbringenden Leistungen
• "Website": die von Netblitz zu entwickelnde oder zu wartende Internetplattform
• "Dienstleistungen": alle von Netblitz durchgeführten Tätigkeiten, einschließlich Webdesign, Webentwicklung, SEO und Wartung`
      },
      {
        icon: Scale,
        title: '2. Anwendbarkeit',
        content: `• Diese Bedingungen gelten für alle Angebote, Kostenvoranschläge und Vereinbarungen
• Abweichungen von diesen Bedingungen sind nur gültig, wenn schriftlich vereinbart
• Etwaige Einkaufs- oder andere Bedingungen des Auftraggebers gelten nicht
• Bei Widersprüchen haben spezifische Projektvereinbarungen Vorrang`
      },
      {
        icon: CreditCard,
        title: '3. Angebote und Preise',
        content: `• Alle Angebote sind unverbindlich und 30 Tage gültig
• Preise verstehen sich ohne MwSt., sofern nicht anders angegeben
• Bei Annahme des Angebots ist eine Anzahlung von 50% vor Arbeitsbeginn fällig
• Der Restbetrag wird bei Projektübergabe in Rechnung gestellt
• Netblitz behält sich das Recht vor, Preise bei Änderungen des Auftrags anzupassen
• Mehrarbeit wird vorab besprochen und zum vereinbarten Stundensatz berechnet
• Die Zahlung erfolgt gemäß der auf der Rechnung angegebenen Zahlungsfrist (Standard 14 Tage)
• Bei Zahlungsverzug ist Netblitz berechtigt, gesetzliche Zinsen zu berechnen`
      },
      {
        icon: Clock,
        title: '4. Ausführung und Lieferzeit',
        content: `• Netblitz führt den Auftrag nach bestem Wissen und Gewissen aus
• Angegebene Lieferzeiten sind unverbindlich und keine festen Fristen
• Verzögerungen durch den Auftraggeber verlängern die Lieferzeit
• Der Auftraggeber ist für die rechtzeitige Lieferung von Texten, Bildern und anderen Materialien verantwortlich
• Netblitz hat das Recht, Dritte für die Ausführung des Auftrags einzusetzen`
      },
      {
        icon: Shield,
        title: '5. Geistiges Eigentum',
        content: `• Das Urheberrecht an von Netblitz entwickelten Designs, Code und Materialien verbleibt bei Netblitz
• Nach vollständiger Bezahlung erhält der Auftraggeber ein Nutzungsrecht für die vereinbarten Zwecke
• Es ist nicht gestattet, die gelieferten Produkte an Dritte zu verkaufen, zu vermieten oder zur Verfügung zu stellen
• Netblitz behält das Recht, die Arbeit für eigene Werbung und Portfolio zu verwenden
• Open-Source-Komponenten behalten ihre ursprüngliche Lizenz`
      },
      {
        icon: AlertTriangle,
        title: '6. Haftung',
        content: `• Netblitz haftet nicht für indirekte Schäden, Folgeschäden oder entgangenen Gewinn
• Die Haftung ist auf den für den betreffenden Auftrag gezahlten Betrag begrenzt
• Netblitz haftet nicht für Schäden durch falsche oder unvollständige Informationen des Auftraggebers
• Der Auftraggeber stellt Netblitz von Ansprüchen Dritter bezüglich gelieferter Inhalte frei
• Netblitz haftet nicht für Ausfallzeiten von Hosting oder externen Diensten`
      },
      {
        icon: XCircle,
        title: '7. Kündigung und Stornierung',
        content: `• Beide Parteien können die Vereinbarung schriftlich mit einer Frist von 30 Tagen kündigen
• Bei Stornierung durch den Auftraggeber sind die bis dahin entstandenen Kosten fällig
• Stornierung nach Projektbeginn erfordert Zahlung von mindestens 50% der Projektsumme
• Netblitz kann die Vereinbarung bei Zahlungsverzug oder Nichterfüllung von Pflichten auflösen
• Nach Beendigung bleiben Geheimhaltungspflichten bestehen`
      },
      {
        icon: Gavel,
        title: '8. Anwendbares Recht',
        content: `• Auf alle Vereinbarungen findet niederländisches Recht Anwendung
• Streitigkeiten werden dem zuständigen Gericht in Maastricht vorgelegt
• Die Parteien werden zunächst versuchen, Streitigkeiten einvernehmlich zu lösen

Netblitz
Robert Schumandomein 2
6229ES Maastricht, Niederlande
E-Mail: hello@netblitz.eu
Telefon: +31 85 115 5703`
      }
    ]
  }
};

export default function TermsPage() {
  const { language } = useLanguage();
  const t = translations[language] || translations.nl;

  return (
    <main className="min-h-screen bg-[#f8f9fc]" data-testid="terms-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#2563EB] to-[#0052e0]">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <FileText className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h1>
            <p className="text-lg opacity-80">{t.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-8 shadow-sm mb-8"
          >
            <p className="text-lg text-[#475569] leading-relaxed">{t.intro}</p>
          </motion.div>

          {/* Sections */}
          <div className="space-y-6">
            {t.sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <section.icon className="w-6 h-6 text-[#2563EB]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-[#0f172a] mb-3">{section.title}</h2>
                    <p className="text-[#475569] whitespace-pre-line leading-relaxed">{section.content}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
